"use client";

import { usePathname } from "next/navigation";
// Note: Using custom breadcrumb implementation since @heroui/breadcrumbs is not available
// We'll create a breadcrumb component that follows HeroUI design patterns
import { useTranslations } from "next-intl";

import { StructuredData, generateBreadcrumbData } from "./structured-data";

import { Link } from "@/i18n/navigation";
import { siteConfig } from "@/config/site";

export interface SEOBreadcrumbsProps {
  customItems?: Array<{
    name: string;
    href: string;
    current?: boolean;
  }>;
  showHome?: boolean;
  className?: string;
  pageTitle?: string; // For dynamic page titles like blog posts
  // Future HeroUI breadcrumb props (prefixed with _ to avoid unused warnings)
  _variant?: "solid" | "bordered" | "light";
  _size?: "sm" | "md" | "lg";
  _color?:
    | "foreground"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  _radius?: "none" | "sm" | "md" | "lg" | "full";
  _separator?: "slash" | "chevron" | "arrow";
  _itemsBeforeCollapse?: number;
  _itemsAfterCollapse?: number;
  _maxItems?: number;
  _renderEllipsis?: (props: any) => React.ReactNode;
  _hideSeparator?: boolean;
  _underline?: "none" | "hover" | "always" | "active" | "focus";
  _isDisabled?: boolean;
}

export function SEOBreadcrumbs({
  customItems,
  showHome = true,
  className,
  pageTitle,
  _variant = "light",
  _size = "md",
  _color = "foreground",
  _radius = "md",
  _separator = "chevron",
  _itemsBeforeCollapse = 1,
  _itemsAfterCollapse = 1,
  _maxItems = 8,
  _renderEllipsis,
  _hideSeparator = false,
  _underline = "hover",
  _isDisabled = false,
}: SEOBreadcrumbsProps) {
  const pathname = usePathname();
  const t = useTranslations("Navigation");
  const tSEO = useTranslations("SEO");

  const generateBreadcrumbItems = () => {
    if (customItems) {
      return customItems;
    }

    // Parse the pathname to generate breadcrumbs
    const segments = pathname.split("/").filter(Boolean);

    // Remove locale from segments (first segment)
    const pathSegments = segments.slice(1);

    const items: Array<{ name: string; href: string; current?: boolean }> = [];

    if (showHome) {
      items.push({
        name: t("Home"),
        href: "/",
        current: pathname === "/" || segments.length === 1,
      });
    }

    let currentPath = "";

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;

      let name: string;

      // For the last segment (current page), use pageTitle if provided
      if (isLast && pageTitle) {
        name = pageTitle;
      } else {
        // Capitalize and format segment name
        const formattedSegment =
          segment.charAt(0).toUpperCase() + segment.slice(1);

        // Try to get translation, but fallback gracefully if not found
        try {
          // First try converting kebab-case to camelCase (e.g., "flight-view" -> "FlightView")
          const camelCaseSegment = formattedSegment.replace(
            /-([a-z])/g,
            (_, letter) => letter.toUpperCase()
          );
          const translatedName = t(camelCaseSegment);

          name = translatedName;
        } catch {
          try {
            // If that fails, try the formatted segment as-is
            let translatedName = t(formattedSegment);

            name = translatedName;
          } catch {
            // If both fail, use formatted segment name
            name = formattedSegment.replace(/-/g, " ");
          }
        }
      }

      items.push({
        name,
        href: currentPath,
        current: isLast,
      });
    });

    return items;
  };

  const breadcrumbItems = generateBreadcrumbItems();

  // Build the locale-prefixed absolute URL for each breadcrumb entry in the
  // JSON-LD. The visible breadcrumb `href` is locale-relative ("/about") so
  // that next-intl's <Link> adds the locale on click — but search-engine
  // structured data must be absolute and locale-prefixed. Falling back to the
  // default locale keeps the schema valid when this component renders outside
  // a locale-prefixed route (e.g. error pages).
  const localeFromPath = pathname.split("/").filter(Boolean)[0];
  const locale =
    localeFromPath && ["en", "es", "fr"].includes(localeFromPath)
      ? localeFromPath
      : "en";

  const structuredDataItems = breadcrumbItems.map(item => ({
    name: item.name,
    url:
      item.href === "/"
        ? `${siteConfig.url}/${locale}`
        : `${siteConfig.url}/${locale}${item.href}`,
  }));

  return (
    <>
      <StructuredData
        data={generateBreadcrumbData(structuredDataItems)}
        type="BreadcrumbList"
      />
      <nav
        aria-label={tSEO("breadcrumb.ariaLabel")}
        className={`mt-4 mb-4 ${className || ""}`}
      >
        <ol className="flex items-center space-x-2 text-sm text-default-600">
          {breadcrumbItems.map((item, index) => (
            <li key={item.href} className="flex items-center">
              {index > 0 && (
                <svg
                  className="h-3 w-3 mx-2 text-default-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M9 5l7 7-7 7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              )}
              {item.current ? (
                <span
                  aria-current="page"
                  className="text-default-900 font-medium"
                >
                  {item.name}
                </span>
              ) : (
                <Link
                  className="text-default-600 hover:text-primary transition-colors"
                  href={item.href}
                >
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}

// Hook for programmatic breadcrumb management
export function useSEOBreadcrumb() {
  const pathname = usePathname();

  const addBreadcrumbItem = (name: string, href?: string) => {
    return {
      name,
      href: href || pathname,
      current: !href,
    };
  };

  const getBreadcrumbData = (items: Array<{ name: string; href: string }>) => {
    return generateBreadcrumbData(
      items.map(item => ({
        name: item.name,
        url: `${siteConfig.url}${item.href}`,
      }))
    );
  };

  return {
    addBreadcrumbItem,
    getBreadcrumbData,
    currentPath: pathname,
  };
}
